// Verhalten der Hülle in der Merxo-App (docs/mobile/SPEZIFIKATION.md §3.4, §4.8, §4.10) – einmal beim Start aus
// web/main.tsx. Im Browser geschieht hier nichts.
//   • Android-Zurück: window.merxoHandleBack schließt die oberste Ebene wie Esc ('handled'), meldet auf der Startseite
//     'root' (App in den Hintergrund) und sonst 'default' (die App geht in der Historie zurück).
//   • Tastatur: Solange ein Eingabefeld den Fokus hat und ein grober Zeiger gilt, trägt <html> data-keyboard – das
//     Telefon-Layout blendet dann die untere Leiste aus (styles/base.css), Eingabe und Hauptaktion bleiben sichtbar.
import { nativeApp, registerNativeBack, type BackResult } from '../lib/native'

/** Startseiten: Zurück legt dort die App in den Hintergrund. */
const ROOT_PATHS = new Set(['/', '/login'])

/** Ebenen, die Esc schließt – Dialoge, Drawer, Sheets, Befehlspalette, Assistent als Blatt, Menüs. */
const LAYER_SELECTOR = '[role="dialog"], [role="alertdialog"], [role="menu"]'

/** Sichtbar: nicht verborgen (hidden, inert, aria-hidden), weder selbst noch ein Vorfahr mit display: none. */
const isShown = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) return false
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false
  if (getComputedStyle(element).visibility === 'hidden') return false
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return false
  }
  return true
}

/** Oberste offene Ebene: offene Auswahlliste vor Menü vor Dialog – sonst die zuletzt eingehängte (Portale). */
export function topmostLayer(root: Document = document): HTMLElement | null {
  const combobox = [...root.querySelectorAll('[role="combobox"][aria-expanded="true"]')].filter(isShown).pop()
  if (combobox instanceof HTMLElement) return combobox
  const layers = [...root.querySelectorAll(LAYER_SELECTOR)].filter(isShown)
  const top = layers.pop()
  return top instanceof HTMLElement ? top : null
}

const pressEscape = (target: HTMLElement): void => {
  const options: KeyboardEventInit = { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true } as KeyboardEventInit
  target.dispatchEvent(new KeyboardEvent('keydown', options))
  target.dispatchEvent(new KeyboardEvent('keyup', options))
}

/** Antwort auf die Zurück-Taste (§3.4). */
export function handleBack(): BackResult {
  const layer = topmostLayer()
  if (layer) {
    // Esc dort auslösen, wo es eine Tastatur auch täte: am Element im Fokus, wenn es in der Ebene liegt
    const focused = document.activeElement
    pressEscape(focused instanceof HTMLElement && layer.contains(focused) ? focused : layer)
    return 'handled'
  }
  return ROOT_PATHS.has(window.location.pathname) ? 'root' : 'default'
}

const TEXT_INPUT_EXCLUDED = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image', 'hidden'])

/** Öffnet dieses Element die Bildschirmtastatur? */
export function opensKeyboard(element: EventTarget | null): boolean {
  if (!(element instanceof HTMLElement)) return false
  if (element instanceof HTMLTextAreaElement) return !element.readOnly
  if (element instanceof HTMLInputElement) return !TEXT_INPUT_EXCLUDED.has(element.type) && !element.readOnly
  return element.isContentEditable
}

/** html[data-keyboard], solange ein Eingabefeld den Fokus hat (nur grober Zeiger). Gibt eine Aufräumfunktion zurück. */
export function watchSoftKeyboard(doc: Document = document): () => void {
  const html = doc.documentElement
  const coarse = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
  const onFocusIn = (event: FocusEvent): void => {
    if (opensKeyboard(event.target) && coarse()) html.dataset.keyboard = ''
  }
  const onFocusOut = (event: FocusEvent): void => {
    // Wechsel von Feld zu Feld: Die Tastatur bleibt stehen
    if (opensKeyboard(event.relatedTarget)) return
    delete html.dataset.keyboard
  }
  doc.addEventListener('focusin', onFocusIn)
  doc.addEventListener('focusout', onFocusOut)
  return () => {
    doc.removeEventListener('focusin', onFocusIn)
    doc.removeEventListener('focusout', onFocusOut)
    delete html.dataset.keyboard
  }
}

/** Einmal beim Start: nur in der App. Gibt eine Aufräumfunktion zurück (Tests). */
export function installNativeShell(): () => void {
  const app = nativeApp()
  if (!app) return () => undefined
  const stopKeyboard = watchSoftKeyboard()
  const stopBack = app.platform === 'android' ? registerNativeBack(handleBack) : () => undefined
  return () => {
    stopKeyboard()
    stopBack()
  }
}
